import { Component, Input, OnDestroy, OnInit, OnChanges, SimpleChanges, HostListener } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LoginRegisterService } from '../login-register/login-register.service';
import { DataService } from '../services/data.service';
import { HeaderService } from './header.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ConfirmDialogComponent } from '../components/confirm-dialog/confirm-dialog.component';


enum PaymentStatus {
  FREETRAIL = 'Free Trial',
  PAID = 'Subscription Active',
  LOCK = 'Free Trial Expired',
  CANCEL_REQUESTED = 'Subscription Inactive'
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})

export class HeaderComponent implements OnInit, OnDestroy, OnChanges {

  @Input() hideHeader: boolean;
  count: number = null;
  loginSuccess: boolean;
  role: string;
  private subs = new Subscription();
  paymentStatus: string = '';
  isProfileActive = false;
  isScrolled = false;

  @HostListener('window:scroll', ['$event'])
  onWindowScroll() {
    this.isScrolled = window.pageYOffset > 0;
  }

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private headerService: HeaderService,
    private loginRegisterService: LoginRegisterService,
    private dataService: DataService,
    private dialog: MatDialog) { }

  ngOnInit(): void {
    this.subs.add(this.headerService.notificationsCount$.subscribe(count => this.count = count));
    // Track route changes to highlight profile icon when in profile/settings
    this.subs.add(
      this.router.events
        .pipe(filter((e: any) => e instanceof NavigationEnd))
        .subscribe((e: NavigationEnd) => this.updateProfileActiveFromUrl(e.urlAfterRedirects || e.url))
    );
    // Initialize active state
    this.updateProfileActiveFromUrl(this.router.url);
    this.subs.add(
      this.loginRegisterService.currentUser.subscribe(res => {
        if (res) {
          this.role = res?.role;
          this.paymentStatus = PaymentStatus[res.paymentStatus];
          this.loginSuccess = true;
          // Add a small delay to ensure hideHeader is properly set by parent
          setTimeout(() => {
            // Only call notificationCount if header is not hidden
            if (!this.shouldHideHeader()) {
              this.notificationCount();
            }
          }, 100);
        } else {
          this.loginSuccess = false;
        }
      })
    );
    this.dataService.cancelSubscriptionClicked$.forEach(event => {
      this.paymentStatus = event;
    });
  }

  openBlog(): void {
    window.open('https://help.evaltrix.com/product', '_blank');
  }

  bookDemo(): void {
    this.router.navigate(['/demo']);
  }

  tryFree(): void {
    this.router.navigate(['/login/employerLogin'], { queryParams: { signupView: true } });
  }

  getStarted(): void {
    this.tryFree();
  }

  logout(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      panelClass: 'delete-dialog-container',
      data: {
        title: 'Logout Confirmation',
        message: 'Are you sure you want to logout?',
        confirmText: 'Logout',
        cancelText: 'Cancel',
        danger: true
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'confirm') {
        this.subs.add(this.loginRegisterService.logout().subscribe(() => {
          // Session data is already cleared in the service
          this.router.navigateByUrl('login');
        }, err => {
          // Even if logout fails, ensure user is redirected to login
          this.toastr.error(err?.error?.errors[0]?.error, 'Error');
          this.router.navigateByUrl('login');
        }));
      }
    });
  }

  onLogoClick(): void {
    if (this.loginSuccess) {
      this.router.navigateByUrl('/dashboard/home');
    } else {
      // If not logged in, navigate to home page
      this.router.navigateByUrl('/');
    }
  }

  private shouldHideHeader(): boolean {
    // Check if hideHeader is explicitly set to true
    if (this.hideHeader === true) {
      return true;
    }

    // Also check current URL as a fallback
    const currentUrl = this.router.url;
    return currentUrl.includes('pendingAssessments')
      || currentUrl.includes('candidate')
      || currentUrl.includes('candidate-welcome')
      || currentUrl.includes('exam-question-view')
      || currentUrl.includes('submitted');
  }

  notificationCount(): void {
    // Don't make API call if header should be hidden (e.g., for candidate routes)
    if (this.shouldHideHeader()) {
      return;
    }

    this.subs.add(this.headerService.notificationCount().subscribe((res) => {
      this.count = res.count;
    }, err => {
      this.toastr.error(err?.error?.errors[0]?.error, 'Error');
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If hideHeader changes and becomes false, and user is logged in, fetch notification count
    if (changes.hideHeader && !changes.hideHeader.firstChange) {
      if (!this.shouldHideHeader() && this.loginSuccess) {
        this.notificationCount();
      }
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private updateProfileActiveFromUrl(url: string): void {
    this.isProfileActive = /\/dashboard\/profile(\b|\/)/.test(url || '');
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case PaymentStatus.FREETRAIL:
        return 'Upgrade';
      case PaymentStatus.LOCK:
        return 'Renew';
      case PaymentStatus.CANCEL_REQUESTED:
        return 'Activate';
      default:
        return '';
    }
  }

  getStatusClass(status: string): string {
    // Use a single gold style for all non-active states
    return 'badge-gold';
  }

  showSubscriptionBadge(): boolean {
    return this.loginSuccess && !!this.paymentStatus && this.paymentStatus !== PaymentStatus.PAID;
  }

  shouldShowPricingLink(): boolean {
    // We rely on the gold upgrade badge to navigate to pricing; hide this link
    return false;
  }

}
